// oil rate surface - Q_o stbd
// gor - GOR - scf/stb

import { Gas_Compressibility_Options_Enum } from "gql/api";
import { LengthUnits } from "../units/length";
import { convert } from "../units";
import { intakeHasGasSeparator } from "./intakes";
import { BarrelsPerDay, CubicFeetPerDay } from "@/libs/units/flowRate";
import { Rankine, TemperatureUnits } from "@/libs/units/temperature";

export function getGasFlowVolumeRatio(
  gas_rate_in_situ: number,
  liquid_rate_in_situ: number
): number {
  const gasFlowVolumeRatio =
    gas_rate_in_situ / (gas_rate_in_situ + liquid_rate_in_situ);
  if (isNaN(gasFlowVolumeRatio)) {
    return 0;
  }
  return gasFlowVolumeRatio;
}

// returns Q_g scfd (gas_phase_gas_rate_from_gor)
// oil_rate stbd
// gor scf/stb
export function getGasFlowRateSCFD(oil_rate: number, gor: number): number {
  return oil_rate * gor;
}

// liquid surface rate - Q_l stbd
// gas liquid ratio - GLR scf/stb
// returns Q_g scfd
export function gas_phase_gas_rate_from_glr(
  fluid_rate: number,
  glr: number
): number {
  return fluid_rate * glr;
}

// gas surface rate - Q_g scfd
// liquid surface rate - Q_l stbd
// returns GLR scf/stb
export function gas_phase_gas_liquid_ratio_surface(
  gas_scf: number,
  liquid_stb: number
): number {
  // GLR should never be <= 0
  const glr = gas_scf / liquid_stb;
  return glr <= 0 ? 0 : glr;
}

// gas surface rate - Q_g scfd
// liquid surface rate - Q_l stbd
// water cut WCut %
// returns GOR scf/stb
export function getGasOilRatioSCFSTB(
  gasFlowRateSCFD: number,
  liquidFlowRateSTDB: number,
  waterCutRatio: number
): number {
  if (liquidFlowRateSTDB === 0 || waterCutRatio === 1) {
    return 0;
  }
  const gor = gasFlowRateSCFD / (liquidFlowRateSTDB * (1 - waterCutRatio));
  return gor <= 0 ? 0 : gor;
}

// gas spec grav - S_gas SpG
// producing pressure at depth - P psia
// temp at depth - T fahrenheit
// reseroir press - P_r psia
// oil gravity - S_oil api
// gor - GOR scf/stb
// compressibliity factor corr - corr_compressibility_factor
// returns Z?
export function gas_phase_compressibility_factor(
  gas_specific_gravity: number,
  producing_pressure_at_depth: number,
  temp_at_depth: number,
  local_pressure: number,
  oil_api: number,
  gor: number,
  corr_compressibility_factor: Gas_Compressibility_Options_Enum | undefined
): number {
  if (
    corr_compressibility_factor === Gas_Compressibility_Options_Enum.BeggsBrill
  ) {
    const H6 =
      678 - 50 * (gas_specific_gravity - 0.5) - 206.7 * 0 + 440 * 0 + 606.7 * 0;
    const H7 =
      326 +
      315.7 * (gas_specific_gravity - 0.5) -
      240 * 0 -
      83.3 * 0 +
      133.3 * 0;
    const H8 = local_pressure / H6;
    const H9 = (temp_at_depth + 460) / H7;
    const H10 = 1.39 * Math.pow(H9 - 0.92, 0.5) - 0.36 * H9 - 0.101;
    const H11 =
      (0.62 - 0.23 * H9) * H8 +
      (0.066 / (H9 - 0.86) - 0.037) * Math.pow(H8, 2) +
      (0.32 / Math.pow(10, 9 * (H9 - 1))) * Math.pow(H8, 6);
    const H12 = 0.132 - 0.32 * Math.log10(H9);
    const H13 = Math.pow(10, 0.3106 - 0.49 * H9 + 0.1824 * Math.pow(H9, 2));
    return H10 + (1 - H10) / Math.exp(H11) + H12 * Math.pow(H8, H13);
  } else if (
    corr_compressibility_factor === Gas_Compressibility_Options_Enum.Cnga
  ) {
    const I6 = Math.log10(
      (Math.pow(gor / gas_specific_gravity, 0.816) *
        Math.pow(temp_at_depth, 0.172)) /
        Math.pow(oil_api, 0.989)
    );
    return (
      1 /
      (1 +
        (local_pressure * 344400 * Math.pow(10, 1.785 * gas_specific_gravity)) /
          Math.pow(temp_at_depth + 460, 3.825))
    );
  } else if (
    corr_compressibility_factor ===
    Gas_Compressibility_Options_Enum.DranchukPurvis
  ) {
    const J6 =
      678 - 50 * (gas_specific_gravity - 0.5) - 206.7 * 0 + 440 * 0 + 606.7 * 0;
    const J7 =
      326 +
      315.7 * (gas_specific_gravity - 0.5) -
      240 * 0 -
      83.3 * 0 +
      133.3 * 0;
    const J8 = local_pressure / J6;
    const J9 = (temp_at_depth + 460) / J7;
    const J10 = -0.101 - 0.36 * J9 + 1.3868 * Math.pow(J9 - 0.919, 0.5);
    const J11 = 0.021 + 0.04275 / (J9 - 0.65);
    const J12 = 0.6222 - 0.224 * J9;
    const J13 = 0.0657 / (J9 - 0.86) - 0.037;
    const J14 = 0.32 * Math.exp(-19.53 * (J9 - 1));
    const J15 = 0.122 * Math.exp(-11.3 * (J9 - 1));
    const J16 = J8 * (J12 + J13 * J8 + J14 * Math.pow(J8, 4));
    return (
      J10 + J11 * J8 + (1 - J10) * Math.exp(-J16) - J15 * Math.pow(J8 / 10, 4)
    );
  } else if (
    corr_compressibility_factor ===
    Gas_Compressibility_Options_Enum.StandingKatz
  ) {
    const K6 =
      678 - 50 * (gas_specific_gravity - 0.5) - 206.7 * 0 + 440 * 0 + 606.7 * 0;
    const K7 =
      326 +
      315.7 * (gas_specific_gravity - 0.5) -
      240 * 0 -
      83.3 * 0 +
      133.3 * 0;
    const K8 = local_pressure / K6;
    const K9 = (temp_at_depth + 460) / K7;
    const K10 = 1.39 * Math.pow(K9 - 0.92, 0.5) - 0.36 * K9 - 0.101;
    const K11 = (0.62 - 0.23 * K9) * K8;
    const K12 = (0.066 / (K9 - 0.86) - 0.037) * Math.pow(K8, 2);
    const K13 = (0.32 / Math.pow(10, 9 * (K9 - 1))) * Math.pow(K8, 6);
    const K14 = K11 + K12 + K13;
    const K15 = 0.132 - 0.32 * Math.log10(K9);
    const K16 = Math.pow(10, 0.3106 - 0.49 * K9 + 0.1824 * Math.pow(K9, 2));
    return K10 + (1 - K10) * Math.exp(-K14) + K15 * Math.pow(K8, K16);
  }
  return 0;
}

/* Used by gas pressure */
// gas spec gravity - S_gas SpG
// pressure in situ - P psia
// temp in situ - T fahrenheit
// N2 %
// CO2 %
// H2S %
// returns Z?
export function gas_phase_compressibility_factor_standings_correlation(
  gas_specific_gravity: number,
  in_situ_pressure: number,
  in_situ_tempf: number,
  n2: number,
  co2: number,
  h2s: number
): number {
  if (gas_specific_gravity > 1.25) {
    gas_specific_gravity = 1.25;
  }
  // else if (gas_specific_gravity < 0.56) {
  //	gas_specific_gravity = 0.56;
  // }
  const n2_frac = n2 / 100;
  const h2s_frac = h2s / 100;
  const co2_frac = co2 / 100;
  // var gas_gravity_at_100 = gas_specific_gravity * (1 + 0.1595 * Math.pow(oil_api, 0.4078) * Math.pow(surface_tempf, -0.2466) * log10((in_situ_pressure + 14.7) / 114.7));
  // var p_c = 706 - (51.7 * gas_gravity_at_100) - 11.1 * gas_gravity_at_100 * gas_gravity_at_100;
  const p_c =
    678 -
    50 * (gas_specific_gravity - 0.5) -
    206.7 * n2_frac +
    440 * co2_frac +
    606.7 * h2s_frac;
  const t_c =
    326 +
    315.7 * (gas_specific_gravity - 0.5) -
    240 * n2_frac -
    83.3 * co2_frac +
    133.3 * h2s_frac;
  const p_r = in_situ_pressure / p_c;
  const t_r = Rankine.toThisUnit(in_situ_tempf);
  const a = -0.101 - 0.36 * t_r + 1.3868 * Math.pow(t_r - 0.919, 0.5);
  const b = 0.021 + 0.04275 / (t_r - 0.65); // (0.62 - (0.23 * t_r)) * p_r + (((0.066 / (t_r - 0.86)) - 0.037) * p_r * p_r) + ((0.32 / Math.pow(10, (9 * (t_r - 1)))) * Math.pow(p_r, 6));
  const d = 0.6222 - 0.224 * p_r; // Math.pow(10, 0.3106 - (0.49 * t_r) + 0.1824 * t_r * t_r);
  const e = 0.0657 / (t_r - 0.86) - 0.037;
  const f = 0.32 * Math.exp(-19.53 * (t_r - 1));
  const h = 0.122 * Math.exp(-11.3 * (t_r - 1));
  const c = p_r * (d + e * p_r + f * Math.pow(p_r, 4));

  // var c = 0.132 - (0.32 * log10(t_r));
  // var z = a + ((1 - a) / Math.exp(b)) + c * Math.pow(p_r, d);
  const z = a + b * p_r + (1 - a) * Math.exp(-c) - h * Math.pow(p_r / 10, 4);
  return z;
}

// pressure - P psia
// temp - T rankine
// compressibility factor Z
// returns B_g cf/scf
export function gas_phase_volume_factor(
  pressure: number,
  tempf: number,
  compressibility_factor: number
  // local_pressure: number,
  // surface_oil_rate: number,
  // surface_water_rate: number,
  // surface_gas_rate: number,
  // oil_specific_gravity: number,
  // gas_specific_gravity: number,
  // water_cut: number,
  // corr_compressability_factor: string
): number {
  const tempr = TemperatureUnits.Rankine.toThisUnit(
    TemperatureUnits.Fahrenheit.toStandardUnit(tempf)
  );
  if (pressure === 0 || isNaN(pressure)) {
    return 0;
  }
  return ((14.696 * compressibility_factor) / pressure) * (tempr / 520); // (14.696 /pressure_compress) * temp_conv;
  //= 14.7*(C12+460)*C53/((C11+14.7)*520)
  // var bg = 14.7 * (tempr) * compressibility_factor / ((pressure + 14.7) * 520);
  // return bg;
}

// gas production rate - Q_g scfd
// gas volume factor - B_g cf/scf
// returns q_g cfd
export function gas_phase_production_rate_with_volume_factor(
  rate: number,
  volume_factor: number
): number {
  return rate * volume_factor;
}

// casing_internal_diameter inches C_id
// intake_outer_diameter inches T_od
// returns annular_space_cross_sectional_area feet^2
export function annular_space_cross_sectional_area(
  casing_internal_diameter: number,
  intake_outer_diameter: number
): number {
  return (
    (Math.PI *
      (convert(
        casing_internal_diameter,
        LengthUnits.Inches,
        LengthUnits.Feet
      ) **
        2 -
        convert(intake_outer_diameter, LengthUnits.Inches, LengthUnits.Feet) **
          2)) /
    4
  );
}

export function gas_separator_efficiency(
  liquid_flow_rate_bbld: number,
  gas_flow_volume_ratio: number,
  max_flow_rate_bpd: number,
  eff1: number,
  eff2: number
): number {
  if (liquid_flow_rate_bbld > max_flow_rate_bpd) return 0;
  if (!intakeHasGasSeparator(eff1, eff2)) return 0;

  const gvfInflection = max_flow_rate_bpd * (1 - gas_flow_volume_ratio);

  const highGasSepEff = liquid_flow_rate_bbld * eff2 + eff1;
  const highGasSepEffAtInflection = gvfInflection * eff2 + eff1;

  const slope =
    -highGasSepEffAtInflection / (max_flow_rate_bpd - gvfInflection);
  const yIntercept = -(slope * max_flow_rate_bpd);

  const transitionGasSepEff = yIntercept + liquid_flow_rate_bbld * slope;

  return liquid_flow_rate_bbld < gvfInflection
    ? Math.max(highGasSepEff, 0)
    : Math.max(transitionGasSepEff, 0);
}

// casing_internal_diameter inches
// intake_outer_diameter inches
// q_l liquid_flow_rate_in_situ bbl/d
// q_g gas_flow_rate_in_situ bbl/d
// rho_l liquid_density lbm/ft^3
// rho_g gas_phase_density lbm/ft^3
// returns N_s
export function gas_natural_separation_efficiency(
  casing_internal_diameter: number,
  intake_outer_diameter: number,
  q_l: number,
  q_g: number,
  rho_l: number,
  rho_g: number
): number {
  const a_ann = annular_space_cross_sectional_area(
    casing_internal_diameter,
    intake_outer_diameter
  );

  const v_sl = (q_l * 0.0000649836034) / a_ann;

  const v_sg = (q_g * 0.0000649836034) / a_ann;

  const bubble_rise_velocity =
    Math.sqrt(2) * Math.pow((1.28 * (rho_l - rho_g)) / (rho_l * rho_l), 0.25);

  let n_s = bubble_rise_velocity / (bubble_rise_velocity + v_sl);

  if (n_s > 100) {
    n_s = 100;
  }
  if (n_s < 0) {
    n_s = 0;
  }

  return n_s;
}

export function getGasRateAfterSeparationBBLD(
  gasFlowRateInSituBBLD: number,
  gasSeparationEfficiency: number
): number {
  const gasFlowRateAfterSeparatationInSituBBLD =
    gasFlowRateInSituBBLD * (1 - gasSeparationEfficiency);

  return gasFlowRateAfterSeparatationInSituBBLD;
}

export function gas_rate_after_separation_pump(
  gas_flow_in_situ_bbld: number,
  liquid_flow_in_situ_bbld: number,
  gas_rate: number,
  gasLiquidRatioSCFSTB: number,
  gas_sep_eff: number,
  gas_volume_factor: number
): number {
  const gas_flow_in_situ_cfd = CubicFeetPerDay.toThisUnit(
    BarrelsPerDay.toStandardUnit(gas_flow_in_situ_bbld)
  );
  const gasLiquidRatioCDFBBL =
    gas_flow_in_situ_cfd /
    liquid_flow_in_situ_bbld; /* Gives Gas/Liquid Ratio as a fraction of CFD to BBL */

  const gasConversionForSeparator = gasLiquidRatioCDFBBL / gasLiquidRatioSCFSTB;

  const gr_after_sep_pump =
    gas_rate -
    (gas_sep_eff * gasConversionForSeparator * gas_rate) / gas_volume_factor;

  return gr_after_sep_pump;
}
